import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { subscribe, unsubscribe } from "../../utils/events";
import { DOCUMENTS_OVERDUE_EVENT, SIGN_BLOCKED_EVENT } from "../../utils/requiredDocsLock";
import ModalRequiredDocsPrompt from "./ModalRequiredDocsPrompt";

export const REQUIRED_DOCS_PROMPT_DISMISSED = "requiredDocsPromptDismissed";

// Delay matches the PSA / received-contracts reminders so the queue keeps one
// convention (see the reminder effects in Layout.js).
const REMINDER_DELAY_MS = 2500;

// The single owner of the required-documents prompt. Three triggers:
//
// 1. Login. Once per session, queued behind the DME reminder, only after
//    onboarding: the days 1-7 reminder, or the locked prompt once the deadline
//    has passed. Same contract as the legacy reminders it replaces for
//    deadline-bearing orgs.
// 2. SIGN_BLOCKED_EVENT — a DME sign entry point refused to sign because the
//    org is locked (the lock gates signing, not the modules).
// 3. DOCUMENTS_OVERDUE_EVENT — the backend refused an order. The DME api
//    wrappers and peptide checkout publish it rather than each call site
//    handling it, so the block produces one prompt and one orgInfo refresh no
//    matter which path hit it.
//
// Event prompts deliberately ignore the session-dismissed key: a user who
// closed the login prompt and then clicks Sign must still see why it failed.
// One boolean backs every event prompt, so racing publishes cannot stack.
//
// A separate component (not inline hooks in Layout) because adding a new hook
// to an existing component reliably breaks CRA Fast Refresh — see CLAUDE.md.
export default function RequiredDocsLockHost({ lock, deferred }) {
  const { auth, refreshOrgInfo } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showLocked, setShowLocked] = useState(false);

  // useAuth rebuilds refreshOrgInfo every render; a ref keeps the event
  // subscriptions from re-attaching on each one.
  const latest = useRef({ refreshOrgInfo });
  latest.current = { refreshOrgInfo };

  useEffect(() => {
    const onSignBlocked = () => setShowLocked(true);
    const onOrderBlocked = () => {
      setShowLocked(true);
      latest.current.refreshOrgInfo?.();
    };
    subscribe(SIGN_BLOCKED_EVENT, onSignBlocked);
    subscribe(DOCUMENTS_OVERDUE_EVENT, onOrderBlocked);
    return () => {
      unsubscribe(SIGN_BLOCKED_EVENT, onSignBlocked);
      unsubscribe(DOCUMENTS_OVERDUE_EVENT, onOrderBlocked);
    };
  }, []);

  useEffect(() => {
    if (!auth?.user) return undefined;
    if (deferred) return undefined;
    if (!lock?.applies || !lock?.hasDeadline) return undefined;
    // Don't prompt before the provider has finished account setup — matches the
    // DME / Required-Docs / PSA reminders.
    if (!auth.user?.custom?.doctorConsentAuthorizeSignatureDate) return undefined;
    if (sessionStorage.getItem(REQUIRED_DOCS_PROMPT_DISMISSED)) return undefined;

    // Re-check at fire time: an event prompt closed during the delay writes the
    // dismissed key, and the login prompt would be a duplicate.
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem(REQUIRED_DOCS_PROMPT_DISMISSED)) setShowLogin(true);
    }, REMINDER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [auth?.user, deferred, lock?.applies, lock?.hasDeadline]);

  // One close for whichever prompt is up: having seen the locked prompt counts
  // as this session's login prompt too, so it is not shown twice.
  const close = () => {
    setShowLogin(false);
    setShowLocked(false);
    sessionStorage.setItem(REQUIRED_DOCS_PROMPT_DISMISSED, "true");
  };

  if (showLocked) return <ModalRequiredDocsPrompt mode="locked" lock={lock} close={close} />;
  if (!showLogin) return null;
  // Mode is read at render: orgInfo can change between the timer firing and
  // the paint.
  return <ModalRequiredDocsPrompt mode={lock?.locked ? "locked" : "reminder"} lock={lock} close={close} />;
}
